import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, FileText } from "lucide-react";

interface LinkedInWizardStep7Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

export function LinkedInWizardStep7({ data, updateData }: LinkedInWizardStep7Props) {
  const isSkipped = data.skipExperienceHighlights || false;

  const toggleSkip = (checked: boolean) => {
    updateData({ 
      skipExperienceHighlights: checked,
      // Clear experience highlights if skipping
      experienceHighlights: checked ? [] : data.experienceHighlights || []
    });
  };
  const addExperience = () => {
    const currentExperience = data.experienceHighlights || [];
    updateData({
      experienceHighlights: [...currentExperience, { role: "", achievements: "" }]
    });
  };

  const removeExperience = (index: number) => {
    const currentExperience = data.experienceHighlights || [];
    const newExperience = currentExperience.filter((_, i) => i !== index);
    updateData({ experienceHighlights: newExperience });
  };

  const updateExperience = (index: number, field: 'role' | 'achievements', value: string) => {
    const currentExperience = data.experienceHighlights || [];
    const newExperience = [...currentExperience];
    newExperience[index] = { ...newExperience[index], [field]: value };
    updateData({ experienceHighlights: newExperience });
  };

  const experiences = data.experienceHighlights || [];

  return (
    <div className="space-y-6">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">7</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">Experience Highlights</h2>
          <p className="text-linkedin/70 mb-6">
            List the 3-5 most relevant roles you've held that we should highlight in your LinkedIn profile.
          </p>
        </div>
      </div>

      {/* Skip Option */}
      <Card className={`p-4 transition-all border-linkedin/20 ${
        isSkipped 
          ? 'bg-green-50 border-green-500 ring-2 ring-green-200 shadow-md' 
          : 'bg-muted/20 border-muted'
      }`}>
        <div className="flex items-start gap-3">
          <Checkbox
            id="skip-experience"
            checked={isSkipped}
            onCheckedChange={toggleSkip}
            className="mt-1"
          />
          <div className="flex-1">
            <Label 
              htmlFor="skip-experience" 
              className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${
                isSkipped ? 'text-green-800 dark:text-green-200' : ''
              }`}
            >
              <FileText className={`w-4 h-4 ${isSkipped ? 'text-green-600' : ''}`} />
              Skip - I've already provided this information in my uploaded documents/notes
            </Label>
            <p className={`text-xs mt-1 ${
              isSkipped ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'
            }`}>
              Check this if your resume, documents, or notes already contain detailed experience information that can be used.
            </p>
          </div>
        </div>
      </Card>

      {isSkipped ? (
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-green-800">
              Experience Section Skipped
            </h3>
            <p className="text-sm text-green-700">
              We'll extract your experience information from the documents and notes you've provided earlier in the wizard.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {experiences.map((experience, index) => (
          <Card key={index} className="p-4 border-linkedin/20">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-linkedin">Experience #{index + 1}</h3>
                {experiences.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeExperience(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`role-${index}`} className="text-sm font-medium text-linkedin">
                  Role/Position *
                </Label>
                <Input
                  id={`role-${index}`}
                  placeholder="Job title"
                  value={experience.role}
                  onChange={(e) => updateExperience(index, 'role', e.target.value)}
                  className="border-linkedin/20 focus:ring-linkedin focus:border-linkedin"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`achievements-${index}`} className="text-sm font-medium text-linkedin">
                  Key Achievements & Wins *
                </Label>
                <Textarea
                  id={`achievements-${index}`}
                  placeholder="Describe your biggest wins, accomplishments, and impact in this role..."
                  value={experience.achievements}
                  onChange={(e) => updateExperience(index, 'achievements', e.target.value)}
                  className="min-h-[100px] border-linkedin/20 focus:ring-linkedin focus:border-linkedin"
                />
                <p className="text-xs text-linkedin/60">
                  Include specific metrics, results, and outcomes whenever possible
                </p>
              </div>
            </div>
          </Card>
            ))}

            <Button
              variant="outline"
              onClick={addExperience}
              className="w-full flex items-center gap-2 border-linkedin text-linkedin hover:bg-linkedin-light"
            >
              <Plus className="w-4 h-4" />
              Add Another Experience
            </Button>
          </div>

          <Card className="p-4 bg-linkedin-light border-linkedin/20">
            <h3 className="font-semibold text-linkedin mb-2">
              Tips for Writing Strong Experience Highlights:
            </h3>
            <ul className="text-linkedin/80 text-sm space-y-1">
              <li>• Start with action verbs (Led, Increased, Developed, Launched)</li>
              <li>• Include specific numbers and metrics (revenue, team size, percentages)</li>
              <li>• Focus on outcomes and impact, not just responsibilities</li>
              <li>• Highlight awards, recognition, or special achievements</li>
              <li>• Mention relevant skills and technologies used</li>
            </ul>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 border-linkedin/20">
              <h4 className="font-medium mb-2 text-linkedin">Example - Good</h4>
              <p className="text-sm text-linkedin/70">
                "Led a cross-functional team of 12 to launch new SaaS product, resulting in $2M ARR within 8 months. Implemented agile methodology that reduced time-to-market by 40%."
              </p>
            </Card>
            <Card className="p-4 border-linkedin/20">
              <h4 className="font-medium mb-2 text-linkedin">Example - Avoid</h4>
              <p className="text-sm text-linkedin/70">
                "Responsible for managing team and working on product launches. Attended meetings and collaborated with different departments."
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}